import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  Lock, Plus, Loader2, CalendarDays, History, RotateCcw, Trash2,
  ChevronDown, ChevronUp, Check, FileText, Printer, Sparkles, AlertTriangle,
} from 'lucide-react';
import { api, formatDate, formatEUR } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { isAdmin, normalizeRole } from '../../lib/roles';
// Una alergia escrita al lado de una casilla que dice «no» no se calla en un
// informe: misma regla que el parte del día y la hoja de anestesia.
import { hayRiesgoEscrito, textoQueContradice } from '../../lib/riesgoClinico';
import {
  listEpisodios, createEpisodio, updateEpisodio, closeEpisodio, reopenEpisodio,
  borrarEpisodio,
  pasarEpisodioALimpio, listCitas, listAgendas, todayStr,
} from '../../lib/citasApi';
import { SectionCard } from '../Common';
import FacialMap from './FacialMap';
import ToothChart from './ToothChart';
import BodyMap from './BodyMap';
import { getClinicProfile } from '../../lib/clinicProfile';
import PatientPhotosTab from './PatientPhotosTab';
import PdfPreviewDialog from '../PdfPreviewDialog';
// Grabación de consulta en cada episodio (26/9/2026).
import GrabacionConsulta, { puedeGrabarConsulta } from './GrabacionConsulta';
import { listarGrabacionesDelPaciente, useGrabacionesTerminadas } from '../../lib/grabadoraConsulta';

/** Historia clínica visual según la especialidad de la clínica (servidor de
 *  demostraciones): odontograma (dental), mapa corporal (fisio) o mapa facial
 *  (cirugía plástica y medicina estética, y también cualquier instalación
 *  real — ahí `getClinicProfile()` siempre resuelve a "plastica"). */
function SpecialtyMap({ patient, onPatientUpdated, apilado = false }) {
  const [profile, setProfile] = useState(null);
  useEffect(() => { getClinicProfile().then(setProfile); }, []);
  if (!profile) return null;
  if (profile === 'dental') return <ToothChart patient={patient} onPatientUpdated={onPatientUpdated} />;
  if (profile === 'fisio') return <BodyMap patient={patient} onPatientUpdated={onPatientUpdated} />;
  return <FacialMap patient={patient} onPatientUpdated={onPatientUpdated} apilado={apilado} />;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import TextareaQueCrece from '../TextareaQueCrece';
import MicButton from '../MicButton';
import ActoMedicoEpisodio from './ActoMedicoEpisodio';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';

// -----------------------------------------------------------------------------
// Historia clínica del paciente: datos clínicos fijos + episodios evolutivos
// (timeline) + citas. Los administrativos VEN todo pero solo admin/dirección/
// doctor pueden crear/editar episodios (el backend también lo valida).
// -----------------------------------------------------------------------------

/**
 * A QUÉ GRUPO PERTENECE CADA CAMPO DE LA TARJETA DE DATOS CLÍNICOS (tercer remate,
 * 2026-09-18). El autoguardado solo manda los campos del GRUPO que el usuario
 * ha tocado; lo demás ni se nombra en el PUT, y lo que no se nombra el servidor
 * no lo escribe (`model_dump(exclude_unset=True)`).
 *
 * La casilla y su caja de texto van JUNTAS a propósito: si alguien responde
 * «No» a las alergias, el detalle se borra —eso lo ha decidido una persona—;
 * pero si no ha tocado esa parte, ni la casilla ni el texto viajan.
 */
const GRUPO_DEL_CAMPO = {
  alergias: 'alergias', alergiasText: 'alergias',
  ah: 'ah', ahText: 'ah',
  med: 'med', medText: 'med',
  historia: 'historia',
  antecedentes: 'antecedentes',
};

/** Los campos de la ficha que guarda la tarjeta de datos clínicos. */
const CAMPOS_CLINICOS_DE_LA_TARJETA = [
  'alergias', 'alergias_text', 'ah_previo', 'ah_previo_text',
  'medicacion_habitual', 'historia_general', 'antecedentes_medicos',
];

/** true/false/null → 'si'/'no'/'' (para los Select Sí/No del formulario). */
const boolToStr = (v) => (v === true ? 'si' : v === false ? 'no' : '');
const strToBool = (s) => (s === 'si' ? true : s === 'no' ? false : null);

/** Desplegable Sí/No directo (sin lápiz). Vacío = sin responder → obligatorio:
 *  el backend no deja abrir ni cerrar la visita hasta que se responde. */
function SiNoSelect({ value, onChange, testId }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5" data-testid={testId}>
        <SelectValue placeholder="— sin responder —" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="si">Sí</SelectItem>
        <SelectItem value="no">No</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// a) Datos clínicos
// ---------------------------------------------------------------------------

function DatosClinicosCard({ patient, onPatientUpdated, esPrimeraConsulta = true }) {
  // Datos clínicos EDITABLES EN LÍNEA (sin lápiz): los desplegables Sí/No
  // guardan al momento; los textos de detalle guardan al salir del campo.
  // Medicación es también Sí/No: "No" se guarda como "Ninguna"; "Sí" pide el
  // detalle. Vacío = sin responder → el backend no deja abrir/cerrar la visita.
  // «No» se escribe de varias maneras según de dónde venga el dato: la ficha
  // pone «Ninguna», el cuestionario de casa/tablet «No refiere (cuestionario)»
  // y el volcado de quirófano «Sin medicación habitual (nota quirófano…)».
  // Todas son NO — antes solo se entendía «Ninguna» y las demás salían como
  // «Sí» con ese texto debajo, incongruente (2026-08-20).
  //
  // Y el «no» tiene que ser TODO el texto, no solo cómo EMPIEZA (tercer remate,
  // 2026-09-18). Al fusionar dos fichas la medicación queda «Ninguna ⏎⏎ — De la
  // ficha fusionada (…) — ⏎ <un anticoagulante>»: empieza por
  // «Ninguna» y dice justo lo contrario. Con el regex de antes el desplegable
  // enseñaba «No», la caja de detalle salía vacía y el primer autoguardado
  // mandaba `medicacion_habitual: 'Ninguna'` — un paciente anticoagulado se
  // quedaba sin su medicación apuntada sin que nadie decidiera nada, y la
  // fusión no se deshace.
  const esMedNegada = (t) => {
    const s = (t || '').trim();
    if (!s) return false;
    // Fuera el paréntesis de DÓNDE viene el dato («(cuestionario)», «(nota
    // quirófano 2026-08-20)»): eso no es medicación.
    const limpio = s.replace(/\((?:nota|parte|cuestionario)[^)]*\)/gi, '')
      .trim().replace(/[.;,]+$/, '');
    return /^(ninguna|ninguno|nada|no|no refiere|no refiere medicaci[óo]n|no toma( nada| medicaci[óo]n)?|sin medicaci[óo]n( habitual)?)$/i.test(limpio);
  };
  const medToStr = (t) => {
    const s = (t || '').trim();
    if (!s) return '';
    return esMedNegada(s) ? 'no' : 'si';
  };
  const [alergias, setAlergias] = useState(boolToStr(patient.alergias));
  const [alergiasText, setAlergiasText] = useState(patient.alergias_text || '');
  const [ah, setAh] = useState(boolToStr(patient.ah_previo));
  const [ahText, setAhText] = useState(patient.ah_previo_text || '');
  const [med, setMed] = useState(medToStr(patient.medicacion_habitual));
  // La misma regla que el desplegable, y no una copia del regex: si las dos se
  // separan, el desplegable dice una cosa y la caja de debajo enseña otra.
  const [medText, setMedText] = useState(
    esMedNegada(patient.medicacion_habitual) ? '' : (patient.medicacion_habitual || ''),
  );
  const [historia, setHistoria] = useState(patient.historia_general || '');
  const [antecedentes, setAntecedentes] = useState(patient.antecedentes_medicos || '');
  const [status, setStatus] = useState(''); // '' | 'saving' | 'saved'
  // ¿Queda algo escrito que todavía no ha llegado al servidor? Se enseña al
  // lado del botón «Guardar» de la historia general (2026-09-09):
  // «Sin guardar» frente a «Guardado ✓», para no salir de la ficha con dudas.
  const [sinGuardar, setSinGuardar] = useState(false);
  // ¿Queda algo escrito que ni siquiera ha SALIDO todavía? Con esto el botón
  // «Guardar» sigue pulsable aunque haya un autoguardado en vuelo: si se
  // apagara solo por estar guardando, escribir y pulsar deprisa perdería el
  // clic — justo lo que el botón viene a evitar.
  const [porEnviar, setPorEnviar] = useState(false);

  // -------------------------------------------------------------------------
  // Guardado de la tarjeta: el autoguardado de siempre (0,8 s tras teclear) MÁS
  // el botón «Guardar», que manda lo escrito al momento. Los dos van por el
  // mismo camino para que NO SE PISEN:
  //   · `datosRef` lleva siempre lo último escrito (setState no es inmediato) y
  //     se apunta al teclear, no cuando toca enviar;
  //   · las peticiones salen EN FILA (`cadenaRef`) y cada una compone el cuerpo
  //     con lo que hay en `datosRef` EN EL MOMENTO DE SALIR, así una petición
  //     vieja no puede escribir encima de un texto más nuevo;
  //   · `versionRef` cuenta los cambios del usuario y `guardadaRef` la última
  //     versión ya confirmada: si no hay nada nuevo no se manda nada, así que
  //     pulsar «Guardar» con un autoguardado en vuelo no duplica el envío.
  // -------------------------------------------------------------------------
  const datosRef = useRef(null);
  if (datosRef.current === null) {
    datosRef.current = { alergias, alergiasText, ah, ahText, med, medText, historia, antecedentes };
  }
  const cadenaRef = useRef(Promise.resolve());
  const versionRef = useRef(0);   // sube con cada cambio del usuario
  const guardadaRef = useRef(0);  // última versión confirmada por el servidor
  const persistTimer = useRef(null);

  // Grupos de la tarjeta que el usuario ha tocado en esta visita a la ficha.
  // Solo eso se manda: ver `GRUPO_DEL_CAMPO`.
  const tocadosRef = useRef(new Set());

  // LO QUE HABÍA EN LA FICHA CUANDO SE ABRIÓ (23/9/2026, segunda pasada del
  // supervisor). Cada guardado manda, de los campos que lleva, el valor que
  // tenían al abrir (`valores_al_abrir`): si otra persona apuntó una alergia
  // entre medias, el servidor contesta 409 en vez de borrarla. Y sin esto el
  // servidor tampoco deja BAJAR un riesgo (quitar la alergia es una decisión
  // del doctor sobre lo que tiene delante). Tras cada guardado bueno, la base
  // pasa a ser lo que contestó el servidor.
  const baseRef = useRef(null);
  if (baseRef.current === null) {
    baseRef.current = Object.fromEntries(CAMPOS_CLINICOS_DE_LA_TARJETA
      .map((campo) => [campo, patient[campo] ?? null]));
  }

  /** Apunta el cambio ya (todavía sin mandarlo) para que lo vea cualquier envío. */
  const aplicar = (patch) => {
    if (!patch || !Object.keys(patch).length) return;
    Object.keys(patch).forEach((campo) => {
      const grupo = GRUPO_DEL_CAMPO[campo];
      if (grupo) tocadosRef.current.add(grupo);
    });
    datosRef.current = { ...datosRef.current, ...patch };
    versionRef.current += 1;
    setSinGuardar(true);
    setPorEnviar(true);
  };

  const enviar = async () => {
    const version = versionRef.current;
    if (version === guardadaRef.current) {
      // Nada nuevo: no se repite la petición, pero se confirma que está todo
      // guardado (igual que hace el editor del evolutivo).
      setStatus('saved');
      setTimeout(() => setStatus((s) => (s === 'saved' ? '' : s)), 1500);
      return;
    }
    const st = datosRef.current;
    // SOLO LO QUE SE HA TOCADO (tercer remate, 2026-09-18). Antes el PUT
    // mandaba los siete campos siempre, y eso borraba a ciegas lo que la
    // pantalla no había sabido leer: tras fusionar, teclear en la historia
    // general mandaba `medicacion_habitual: 'Ninguna'` encima de la medicación
    // que había pegado la fusión, y `alergias_text: ''` encima de una alergia
    // apuntada en otra nota (la casilla estaba en «no» porque el paciente ya
    // había contestado «no» en el cuestionario). Nadie decidía nada: bastaba
    // abrir la pestaña y escribir en otro campo. En duda, el riesgo se APUNTA,
    // nunca se borra.
    const tocados = tocadosRef.current;
    const cuerpo = {};
    if (tocados.has('alergias')) {
      cuerpo.alergias = strToBool(st.alergias);
      cuerpo.alergias_text = st.alergias === 'si' ? (st.alergiasText || '') : '';
    }
    if (tocados.has('ah')) {
      cuerpo.ah_previo = strToBool(st.ah);
      cuerpo.ah_previo_text = st.ah === 'si' ? (st.ahText || '') : '';
    }
    if (tocados.has('med')) {
      cuerpo.medicacion_habitual =
        st.med === 'no' ? 'Ninguna' : (st.med === 'si' ? (st.medText || '') : '');
    }
    if (tocados.has('historia')) cuerpo.historia_general = st.historia || '';
    if (tocados.has('antecedentes')) cuerpo.antecedentes_medicos = st.antecedentes || '';
    if (!Object.keys(cuerpo).length) {
      // No se ha tocado nada de lo que se guarda aquí: no se manda un PUT vacío.
      guardadaRef.current = version;
      setSinGuardar(false);
      setStatus('saved');
      setTimeout(() => setStatus((s) => (s === 'saved' ? '' : s)), 1500);
      return;
    }
    setStatus('saving');
    setPorEnviar(false);   // ya va de camino: lo nuevo que se escriba lo vuelve a poner
    const valoresAlAbrir = Object.fromEntries(Object.keys(cuerpo)
      .map((campo) => [campo, baseRef.current[campo] ?? null]));
    try {
      const res = await api.put(`/api/patients/${patient.id}`,
        { ...cuerpo, valores_al_abrir: valoresAlAbrir });
      const guardada = (res?.data && res.data.id === patient.id) ? res.data : cuerpo;
      CAMPOS_CLINICOS_DE_LA_TARJETA.forEach((campo) => {
        if (campo in guardada) baseRef.current[campo] = guardada[campo] ?? null;
      });
      guardadaRef.current = version;
      // Si ha seguido escribiendo mientras iba la petición, sigue habiendo
      // cambios sin guardar (los recoge el autoguardado que ya está puesto).
      if (versionRef.current === version) setSinGuardar(false);
      setStatus('saved');
      onPatientUpdated?.();
      setTimeout(() => setStatus((s) => (s === 'saved' ? '' : s)), 1500);
    } catch (e) {
      setStatus('');
      setPorEnviar(true);  // no llegó: se puede reintentar con el botón
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar los datos clínicos');
    }
  };

  /** Pone una petición en la cola: nunca salen dos a la vez. */
  const encolar = () => {
    cadenaRef.current = cadenaRef.current.then(enviar).catch(() => {});
    return cadenaRef.current;
  };

  const persist = (patch = {}) => { aplicar(patch); return encolar(); };

  // Guarda los textos de detalle SEGÚN se escriben (0,8 s tras teclear), no solo
  // al salir del campo: así no se pierde nada si se cambia de paciente rápido.
  const persistDebounced = (patch) => {
    aplicar(patch);
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => { persistTimer.current = null; encolar(); }, 800);
  };

  /** Botón «Guardar»: manda YA, sin esperar al autoguardado, y se lleva por
   *  delante el autoguardado pendiente para no mandar dos veces lo mismo. */
  const guardarAhora = () => {
    if (persistTimer.current) { clearTimeout(persistTimer.current); persistTimer.current = null; }
    return encolar();
  };

  useEffect(() => () => { if (persistTimer.current) clearTimeout(persistTimer.current); }, []);

  return (
    <SectionCard
      title="Datos clínicos"
      action={
        status === 'saving' ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>
        ) : status === 'saved' ? (
          <span className="text-[11px] text-[#2E6E5E] font-medium">Guardado ✓</span>
        ) : null
      }
    >
      {/* Alergias, medicación, antecedentes e historia: enteros, sin barra
          propia (24/9/2026). */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-3 text-xs" data-testid="datos-clinicos">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Alergias <span className="text-red-600">*</span></div>
          <SiNoSelect value={alergias} onChange={(v) => { setAlergias(v); persist({ alergias: v }); }} testId="datos-clinicos-alergias" />
          {alergias === 'si' && (
            <TextareaQueCrece
              value={alergiasText}
              onChange={(e) => { const v = e.target.value; setAlergiasText(v); persistDebounced({ alergiasText: v }); }}
              onBlur={() => persist()}
              rows={2}
              placeholder="Ej.: Penicilina, látex…"
              className="mt-1.5 text-xs"
              data-testid="datos-clinicos-alergias-text"
            />
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Ác. hialurónico previo <span className="text-red-600">*</span></div>
          <SiNoSelect value={ah} onChange={(v) => { setAh(v); persist({ ah: v }); }} testId="datos-clinicos-ah-previo" />
          {ah === 'si' && (
            <TextareaQueCrece
              value={ahText}
              onChange={(e) => { const v = e.target.value; setAhText(v); persistDebounced({ ahText: v }); }}
              onBlur={() => persist()}
              rows={2}
              placeholder="Ej.: Ácido hialurónico en surcos (2023)…"
              className="mt-1.5 text-xs"
              data-testid="datos-clinicos-ah-previo-text"
            />
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">¿Toma medicación? <span className="text-red-600">*</span></div>
          <SiNoSelect value={med} onChange={(v) => { setMed(v); persist({ med: v }); }} testId="datos-clinicos-medicacion" />
          {med === 'si' && (
            <TextareaQueCrece
              value={medText}
              onChange={(e) => { const v = e.target.value; setMedText(v); persistDebounced({ medText: v }); }}
              onBlur={() => persist()}
              rows={2}
              placeholder="¿Cuál? Ej.: Sintrom, omeprazol…"
              className="mt-1.5 text-xs"
              data-testid="datos-clinicos-medicacion-text"
            />
          )}
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Antecedentes médicos</div>
            <MicButton
              onText={(t) => { const v = antecedentes ? `${antecedentes.trim()} ${t}` : t; setAntecedentes(v); persistDebounced({ antecedentes: v }); }}
              className="h-6 w-6"
              title="Dictar antecedentes"
            />
          </div>
          <TextareaQueCrece
            value={antecedentes}
            onChange={(e) => { const v = e.target.value; setAntecedentes(v); persistDebounced({ antecedentes: v }); }}
            onBlur={() => persist()}
            rows={2}
            placeholder="Opcional — diabetes, HTA, cardiopatía, anticoagulación, cirugías previas…"
            className="mt-1.5 text-xs"
            data-testid="datos-clinicos-antecedentes-medicos"
          />
        </div>

        <div className="col-span-2">
          {/* Cabecera de la historia general: a la altura del título y a su
              derecha, el estado y el botón «Guardar» (2026-09-09).
              El autoguardado sigue igual; el botón es la garantía de que lo
              escrito queda registrado antes de salir de la ficha. */}
          <div className="flex items-center justify-between gap-2" data-testid="historia-general-cabecera">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Historia general</div>
            <div className="flex items-center gap-2">
              <span className="text-[10px]" aria-live="polite" data-testid="historia-general-estado">
                {status === 'saving' ? (
                  <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>
                ) : sinGuardar ? (
                  <span className="font-medium text-amber-700">Sin guardar</span>
                ) : (
                  <span className="text-muted-foreground">Guardado ✓</span>
                )}
              </span>
              <MicButton
                onText={(t) => { const v = historia ? `${historia.trim()} ${t}` : t; setHistoria(v); persistDebounced({ historia: v }); }}
                className="h-6 w-6"
                title="Dictar historia general"
              />
              <Button
                size="sm"
                className="h-7 px-2.5 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                onClick={guardarAhora}
                disabled={status === 'saving' && !porEnviar}
                data-testid="datos-clinicos-guardar"
              >
                Guardar
              </Button>
            </div>
          </div>
          {/* Entera, sin barra propia (24/9/2026): el recuadro crece
              con el texto en vez de quedarse en tres líneas con scroll. El
              mínimo (78px) es el alto de las tres líneas de antes. */}
          <TextareaQueCrece
            value={historia}
            onChange={(e) => { const v = e.target.value; setHistoria(v); persistDebounced({ historia: v }); }}
            onBlur={() => persist()}
            rows={3}
            placeholder="Antecedentes, cirugías previas, observaciones… (aquí se ve también lo que escribe enfermería)"
            className="mt-1.5 text-xs min-h-[78px]"
            data-testid="datos-clinicos-historia-general"
          />
        </div>

        {/* Lo que vino del programa anterior al importar las fichas. No se
            enseñaba en ninguna pantalla y para un paciente importado puede
            ser su ÚNICO antecedente (2026-08-18). Se muestra SIN importes: el dinero está en Cobros y
            Presupuestos. Es solo lectura — es un dato histórico, no se edita. */}
        {patient.notas_importacion && (
          <div className="col-span-2" data-testid="notas-importacion">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Notas de la ficha antigua
            </div>
            <div className="mt-1.5 rounded-md border border-dashed border-border bg-muted/40 p-2.5 text-xs whitespace-pre-wrap break-words text-foreground/90">
              {patient.notas_importacion}
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              Traído de la ficha anterior. No se puede editar.
            </p>
          </div>
        )}

      </div>

    </SectionCard>
  );
}

/**
 * Marca de IVA de la primera valoración (2026-08-04).
 *
 * Antes esto era un formulario de "Venta de presupuesto" con precio y botón de
 * guardar. Fuera: el precio y el concepto salen del EVOLUTIVO que escribe el
 * médico, como siempre (basta con «presu» y el importe). Aquí queda solo lo que
 * hay que dejar por escrito sí o sí: con IVA o sin IVA. Y solo en la primera
 * valoración, no en las revisiones.
 */
function MarcaIvaPrimeraValoracion({ patient, episodio, onPatientUpdated }) {
  const { user } = useAuth();
  const [iva, setIva] = useState(patient?.iva === 'con' ? true : patient?.iva === 'sin' ? false : null);
  const [saving, setSaving] = useState(false);

  // Exento en su ficha de usuario (2026-08-19): ni tarjeta ni
  // obligación al cerrar — el servidor aplica la misma exención.
  if (user?.exento_marca_iva) return null;

  const marcar = async (valor) => {
    setSaving(true);
    try {
      await api.post(`/api/episodios/${episodio.id}/iva`, { vat_applied: valor });
      setIva(valor);
      toast.success(valor ? 'Marcado: con IVA' : 'Marcado: sin IVA · queda la constancia en el evolutivo');
      onPatientUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className={`cp-card p-3 ${iva === null ? 'border-2 border-amber-400 bg-amber-50/40' : 'border border-[#2E6E5E]/40 bg-[rgba(46,110,94,0.04)]'}`}
      data-testid="episodio-marca-iva"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-semibold text-[#13231E]">
            ¿El tratamiento lleva IVA?
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {iva === null
              ? 'Hay que marcarlo antes de cerrar la visita.'
              : 'El precio sale de lo que escribas en el evolutivo.'}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => marcar(true)}
            disabled={saving}
            className={`px-3 py-2 rounded-md border text-sm font-medium disabled:opacity-60 ${iva === true ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]' : 'bg-white text-[#13231E] border-border hover:bg-muted'}`}
            data-testid="episodio-con-iva"
          >Con IVA</button>
          <button
            type="button"
            onClick={() => marcar(false)}
            disabled={saving}
            className={`px-3 py-2 rounded-md border text-sm font-medium disabled:opacity-60 ${iva === false ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-[#13231E] border-border hover:bg-muted'}`}
            data-testid="episodio-sin-iva"
          >Sin IVA</button>
          {saving && <Loader2 className="w-4 h-4 animate-spin self-center text-muted-foreground" />}
        </div>
      </div>
      {iva === false && (
        <div className="mt-2 text-xs text-amber-900" data-testid="episodio-aviso-reconstructiva">
          Sin IVA: se ha dejado constancia en el evolutivo del motivo de la exención (el de Configuración).
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// b) Episodio abierto que este usuario puede editar (el suyo, y el de
//    cualquiera si es admin) — modo edición con autosave (debounce 2 s)
// ---------------------------------------------------------------------------

// QUE DOS PERSONAS NO SE PISEN LA HISTORIA CLÍNICA (2026-09-17)
// ---------------------------------------------------------------------------
// Desde que un admin puede corregir el episodio ABIERTO de un médico, el día a
// día es el médico escribiendo en consulta y mostrador corrigiendo la misma
// nota. Este editor manda el textarea ENTERO en cada autoguardado, así que
// hasta hoy el último en guardar borraba lo del otro en silencio y con un
// «Guardado ✓» verde. Tres cosas lo cierran:
//
//   1. cada guardado lleva el SELLO (`updated_at`) de la versión que se leyó;
//      si ha cambiado por debajo, el servidor contesta 409 y NO escribe nada;
//   2. eso se convierte en un aviso grande DENTRO de la tarjeta, nunca en un
//      diálogo: el médico está en consulta con el paciente delante y un modal
//      le robaría el foco y el teclado. Su texto se queda en el recuadro, tal
//      cual, y decide él con las dos versiones a la vista;
//   3. el editor deja de fiarse de lo que leyó al montarse: cada 20 s vuelve a
//      mirar el episodio, y si ha cambiado sin que él tenga nada pendiente, se
//      trae el texto nuevo diciendo de quién es.
//
// Y lo que no llega a guardarse NO se pierde: antes de mandar el último texto
// (incluido el del desmontaje, que antes tenía un `.catch(() => {})` que se
// tragaba el 403 de «lo han cerrado») se deja una copia en este navegador, que
// se borra sola en cuanto el servidor confirma. Si falló, al volver al episodio
// se ofrece recuperarla.

/** Cuerpo del PUT: el texto y el sello de la versión sobre la que se escribió.
 *  Sin sello se manda solo el texto — el servidor lo acepta como siempre. */
function cuerpoDelPut(texto, sello) {
  return sello ? { texto, visto_updated_at: sello } : { texto };
}

const claveBorrador = (episodioId) => `cp-evolutivo-sin-guardar-${episodioId}`;

/** Copia de rescate de lo que todavía no ha llegado al servidor. */
function guardarBorrador(episodioId, texto) {
  try {
    if (!texto) localStorage.removeItem(claveBorrador(episodioId));
    else localStorage.setItem(claveBorrador(episodioId),
      JSON.stringify({ texto, cuando: new Date().toISOString() }));
  } catch { /* modo privado o sin espacio: el aviso en pantalla sigue estando */ }
}

function leerBorrador(episodioId) {
  try {
    const crudo = localStorage.getItem(claveBorrador(episodioId));
    const b = crudo ? JSON.parse(crudo) : null;
    return b && typeof b.texto === 'string' ? b : null;
  } catch { return null; }
}

function olvidarBorrador(episodioId) {
  try { localStorage.removeItem(claveBorrador(episodioId)); } catch { /* ídem */ }
}

/** Cada cuánto vuelve a mirar el editor si la nota ha cambiado por debajo. */
const REPASO_MS = 20000;

function EpisodioEditor({ episodio, patient, ajeno = false, onChanged, pie = null }) {
  const [texto, setTexto] = useState(episodio.texto || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [limpiando, setLimpiando] = useState(false);
  const [propuesta, setPropuesta] = useState(null); // texto redactado por la IA
  const [ejemplos, setEjemplos] = useState(0);      // evolutivos suyos usados
  const timerRef = useRef(null);
  const textoRef = useRef(episodio.texto || '');
  const savedRef = useRef(episodio.texto || ''); // último texto YA persistido
  const taRef = useRef(null);
  // Sello (`updated_at`) de la versión sobre la que estamos escribiendo: viaja
  // en cada guardado para que el servidor pueda frenar un pisotón.
  const baseRef = useRef(episodio.updated_at || null);
  const savingRef = useRef(false);
  // Lo escrito NO está guardado y hay que decirlo:
  //   {tipo: 'conflicto'|'bloqueado'|'fallo', mensaje, quien, textoServidor, sello}
  const [aviso, setAviso] = useState(null);
  const avisoRef = useRef(null);   // para consultarlo sin rehacer los callbacks
  // Texto de un guardado que no llegó a entrar, rescatado de este navegador.
  const [rescate, setRescate] = useState(null);
  // Alguien cambió la nota y, al no haber nada pendiente, se ha traído sola.
  const [refrescado, setRefrescado] = useState(null);

  const ponerAviso = useCallback((a) => { avisoRef.current = a; setAviso(a); }, []);

  /** Un guardado que NO ha entrado: copia de lo escrito, aviso a la vista y se
   *  deja de autoguardar hasta que la persona decida. Lo que nunca se hace es
   *  tragárselo: lo que se pierde aquí es historia clínica. */
  const anotarFallo = useCallback((e, t) => {
    guardarBorrador(episodio.id, t);
    const status = e?.response?.status;
    const conflicto = e?.response?.data?.conflicto;
    const detalle = e?.response?.data?.detail;
    if (status === 409) {
      ponerAviso({
        tipo: 'conflicto',
        quien: conflicto?.quien || 'otra persona',
        textoServidor: conflicto?.texto || '',
        sello: conflicto?.updated_at || null,
        mensaje: detalle || 'Otra persona ha cambiado este evolutivo mientras lo tenías abierto.',
      });
      toast.warning('Tu texto del evolutivo NO se ha guardado', {
        description: 'Lo ha cambiado otra persona mientras lo tenías abierto. Mira el aviso del episodio: lo tuyo sigue ahí.',
        duration: 12000,
      });
      return;
    }
    const bloqueado = status === 403;
    ponerAviso({
      tipo: bloqueado ? 'bloqueado' : 'fallo',
      mensaje: detalle || (bloqueado
        ? 'Este episodio ya no admite cambios.'
        : 'No se pudo guardar el episodio.'),
    });
    toast.error(bloqueado
      ? 'Tu corrección del evolutivo NO se ha guardado'
      : (detalle || 'No se pudo guardar el episodio'), {
      description: 'Lo que has escrito sigue en pantalla y guardado en este navegador.',
      duration: 12000,
    });
  }, [episodio.id, ponerAviso]);

  // Alto mínimo del recuadro: EL DE LA COLUMNA DEL MAPA de al lado, para que
  // escribir y cara queden a la par sea cual sea la pantalla (2026-08-20,
  // Dirección: «proporcional al tamaño de la cara», un número fijo es genérico).
  // ~150px son la cabecera y la botonera del propio editor. En pantallas sin
  // columnas (el mapa debajo), mínimo discreto como siempre.
  const [minAlto, setMinAlto] = useState(96);
  useEffect(() => {
    const mapa = document.querySelector('[data-columna-mapa]');
    if (!mapa) return undefined;
    const medir = () => {
      const dosColumnas = window.matchMedia('(min-width: 1280px)').matches;
      // Un poco más corto que la columna del mapa para que la botonera con
      // «Guardar» quede a la vista sin hacer scroll (2026-08-20).
      setMinAlto(dosColumnas ? Math.max(mapa.clientHeight - 240, 260) : 96);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(mapa);
    return () => ro.disconnect();
  }, []);

  // Autosize del textarea según contenido.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight + 2, minAlto)}px`;
  }, [texto, minAlto]);

  const doSave = useCallback(async (t) => {
    // Con un aviso puesto no sale nada: reintentar a ciegas repetiría el error
    // y taparía el aviso a base de toasts. Se sale de aquí al resolverlo.
    if (avisoRef.current) return;
    if (t === savedRef.current) { setSaved(true); return; }
    setSaving(true);
    savingRef.current = true;
    try {
      const fresco = await updateEpisodio(episodio.id, cuerpoDelPut(t, baseRef.current));
      savedRef.current = t;
      // El sello nuevo es sobre el que escribirá el siguiente guardado.
      if (fresco?.updated_at) baseRef.current = fresco.updated_at;
      olvidarBorrador(episodio.id);
      setRefrescado(null);
      setSaved(true);
    } catch (e) {
      anotarFallo(e, t);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [episodio.id, anotarFallo]);

  // Vuelca YA lo pendiente (al salir del campo o al pulsar Guardar).
  const flush = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (textoRef.current !== savedRef.current) doSave(textoRef.current);
  }, [doSave]);

  // Clave: al DESMONTAR (cambiar de paciente/pestaña/cerrar la ficha) NO se
  // pierde lo tecleado — se envía el último texto aunque el debounce no haya
  // saltado. La petición sale aunque el componente desaparezca.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const pendiente = textoRef.current;
    if (pendiente === savedRef.current) return;
    // La copia se deja ANTES de mandar: si el guardado falla —o la pestaña se
    // cierra por el camino— lo escrito sigue estando en algún sitio.
    guardarBorrador(episodio.id, pendiente);
    updateEpisodio(episodio.id, cuerpoDelPut(pendiente, baseRef.current))
      .then(() => olvidarBorrador(episodio.id))
      .catch((e) => {
        // Aquí ya no hay tarjeta donde pintar el aviso, pero el toast es global
        // y sobrevive al desmontaje. Antes había un `.catch(() => {})` que se
        // tragaba justo esto: el 403 de «el médico ha cerrado el episodio
        // mientras lo corregías». Un fallo al guardar historia clínica no se
        // puede quedar en nada.
        toast.error('Tu texto del evolutivo no ha llegado a guardarse', {
          description: `${e?.response?.data?.detail || 'No se pudo guardar.'} Lo he dejado guardado en este navegador: vuelve a la historia del paciente para recuperarlo.`,
          duration: 15000,
        });
      });
  }, [episodio.id]);

  // ¿Quedó algo sin guardar de la última vez? No se toca el recuadro por las
  // bravas: se enseña aparte y decide la persona.
  useEffect(() => {
    const b = leerBorrador(episodio.id);
    if (b && b.texto && b.texto !== textoRef.current) setRescate(b);
    else if (b) olvidarBorrador(episodio.id);
  }, [episodio.id]);

  /** Lo que hay AHORA en el servidor, venga del repaso o de una recarga de la
   *  pestaña. Aquí es donde el editor deja de fiarse de lo que leyó al montarse. */
  const atender = useCallback((fresco) => {
    if (!fresco || savingRef.current || avisoRef.current) return;
    const sello = fresco.updated_at || null;
    if (!sello || sello === baseRef.current) return;
    // Una lista que salió ANTES de nuestro último guardado no puede deshacerlo:
    // si llega tarde, se tira. Si no, un repaso en vuelo devolvería el texto de
    // antes al recuadro justo después de guardar.
    const cuando = Date.parse(sello);
    const cuandoBase = Date.parse(baseRef.current || '');
    if (Number.isFinite(cuando) && Number.isFinite(cuandoBase) && cuando < cuandoBase) return;
    const pendiente = textoRef.current !== savedRef.current;
    if (fresco.status !== 'abierto') {
      // Lo han cerrado por debajo. Sin nada pendiente no hay drama: que la
      // pestaña se recargue y quede como lo que es, una nota cerrada.
      if (!pendiente) { baseRef.current = sello; onChanged?.(); return; }
      guardarBorrador(episodio.id, textoRef.current);
      ponerAviso({
        tipo: 'bloqueado',
        mensaje: 'Han cerrado este episodio mientras lo corregías: lo que has escrito NO ha entrado.',
      });
      toast.error('Han cerrado el evolutivo mientras lo corregías', {
        description: 'Tu texto sigue en pantalla. Cópialo: para meterlo hay que reabrir el episodio.',
        duration: 15000,
      });
      return;
    }
    if (pendiente) {
      ponerAviso({
        tipo: 'conflicto',
        quien: fresco.updated_by_name || 'otra persona',
        textoServidor: fresco.texto || '',
        sello,
        mensaje: `${fresco.updated_by_name || 'Otra persona'} ha cambiado este evolutivo mientras lo tenías abierto. Lo que escribas encima NO se está guardando.`,
      });
      toast.warning('Este evolutivo ha cambiado mientras lo tenías abierto', {
        description: 'Tu texto sigue en pantalla, sin guardar. Mira el aviso del episodio.',
        duration: 12000,
      });
      return;
    }
    // Sin nada pendiente se adopta lo nuevo: es el único momento en el que se
    // puede cambiar el recuadro sin quitarle nada a nadie.
    setTexto(fresco.texto || '');
    textoRef.current = fresco.texto || '';
    savedRef.current = fresco.texto || '';
    baseRef.current = sello;
    setRefrescado(fresco.updated_by_name || 'otra persona');
  }, [episodio.id, onChanged, ponerAviso]);

  // Repaso cada 20 s (solo con la pestaña a la vista y sin nada en vuelo).
  useEffect(() => {
    const pacienteId = episodio.patient_id;
    if (!pacienteId) return undefined;
    const t = setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (savingRef.current || avisoRef.current) return;
      try {
        const lista = await listEpisodios(pacienteId);
        atender((Array.isArray(lista) ? lista : []).find((x) => x.id === episodio.id));
      } catch { /* si la red falla, en el próximo repaso */ }
    }, REPASO_MS);
    return () => clearInterval(t);
  }, [episodio.id, episodio.patient_id, atender]);

  // Y si la pestaña recarga los episodios por su cuenta, lo mismo sin esperar.
  useEffect(() => { atender(episodio); }, [episodio, atender]);

  const onChangeText = (e) => {
    const t = e.target.value;
    setTexto(t);
    textoRef.current = t;
    setSaved(false);
    setRefrescado(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSave(t), 800); // autosave 0,8 s tras teclear
  };

  // --- Salidas del aviso: las tres las elige la persona, nunca la pantalla ---

  /** Guardar lo mío ENCIMA de lo que hay: decisión tomada con las dos
   *  versiones delante, que es muy distinto de pisarlo sin enterarse. */
  const guardarLoMioEncima = () => {
    const a = avisoRef.current;
    if (a?.sello) baseRef.current = a.sello;   // ahora escribimos sobre lo suyo
    ponerAviso(null);
    doSave(textoRef.current);
  };

  /** Quedarme con lo que hay guardado: lo mío se va, pero lo he leído antes. */
  const quedarmeConLoGuardado = () => {
    const a = avisoRef.current;
    const t = a?.textoServidor || '';
    setTexto(t);
    textoRef.current = t;
    savedRef.current = t;
    if (a?.sello) baseRef.current = a.sello;
    olvidarBorrador(episodio.id);
    ponerAviso(null);
    setSaved(true);
  };

  /** Volver a intentarlo (fallo de red o del servidor, no un pisotón). */
  const reintentarGuardado = () => {
    ponerAviso(null);
    doSave(textoRef.current);
  };

  /** Copiar lo escrito para pegarlo donde haga falta cuando ya no se puede
   *  guardar aquí (episodio cerrado por debajo). */
  const copiarLoMio = async () => {
    try {
      await navigator.clipboard.writeText(textoRef.current || '');
      toast.success('Copiado: pégalo donde lo necesites');
    } catch {
      toast.error('El navegador no ha dejado copiar — selecciónalo a mano');
    }
  };

  /** Ya lo he resuelto por mi cuenta: quita el aviso (la copia de rescate se
   *  queda por si acaso, y vuelve a ofrecerse al abrir el episodio). */
  const cerrarAviso = () => ponerAviso(null);

  /** Recuperar el texto que no llegó a guardarse la última vez. */
  const usarElRescate = () => {
    const t = rescate?.texto || '';
    setRescate(null);
    onChangeText({ target: { value: t } });
  };

  const descartarElRescate = () => {
    olvidarBorrador(episodio.id);
    setRescate(null);
  };

  // Dictado por voz: se añade al final del evolutivo por el mismo camino que
  // teclear (misma lógica de autoguardado y autosize).
  const appendTexto = (dictado) => {
    const cur = textoRef.current || '';
    const next = cur ? `${cur.replace(/\s+$/, '')} ${dictado}` : dictado;
    onChangeText({ target: { value: next } });
  };

  const onManualSave = () => {
    const a = avisoRef.current;
    if (a && a.tipo === 'fallo') { reintentarGuardado(); return; }
    if (a) {
      // Con un aviso puesto, «Guardar» no puede hacer como si nada.
      toast.warning(a.tipo === 'bloqueado'
        ? 'Este episodio ya no admite cambios: copia tu texto, hay que reabrirlo para meterlo'
        : 'Antes decide qué hacer con el aviso: tu texto no se está guardando');
      return;
    }
    flush();
  };

  // Pasar a limpio (2026-07-26): la IA redacta lo dictado imitando cómo escribe
  // este mismo doctor en sus evolutivos anteriores. Nunca sustituye sola —
  // primero se ve la propuesta al lado del original y él decide.
  const pedirLimpio = async () => {
    const t = (textoRef.current || '').trim();
    if (!t) return toast.info('Dicta o escribe algo primero');
    setLimpiando(true);
    try {
      const r = await pasarEpisodioALimpio(episodio.id, t);
      setPropuesta(r.texto);
      setEjemplos(r.ejemplos_usados ?? 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo pasar a limpio');
    } finally {
      setLimpiando(false);
    }
    return undefined;
  };

  const aceptarLimpio = () => {
    onChangeText({ target: { value: propuesta } });
    setPropuesta(null);
    toast.success('Evolutivo pasado a limpio');
  };

  const onConfirmClose = async () => {
    setClosing(true);
    try {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      // Cerrar deja la nota bloqueada: si lo último escrito no ha entrado, NO
      // se cierra. Un episodio cerrado con el texto del otro y sin el tuyo
      // sería exactamente el fallo que esto viene a tapar.
      if (avisoRef.current) {
        setConfirmOpen(false);
        toast.warning('No se ha cerrado: antes resuelve el aviso, tu texto no está guardado');
        return;
      }
      if (textoRef.current !== savedRef.current) {
        try {
          const fresco = await updateEpisodio(episodio.id, cuerpoDelPut(textoRef.current, baseRef.current));
          savedRef.current = textoRef.current;
          if (fresco?.updated_at) baseRef.current = fresco.updated_at;
          olvidarBorrador(episodio.id);
        } catch (e) {
          anotarFallo(e, textoRef.current);
          setConfirmOpen(false);
          return;
        }
      }
      const cerrado = await closeEpisodio(episodio.id);
      // Primera valoración con cirugía y precio en la nota: el presupuesto se
      // ha preparado solo y espera en la pestaña Presupuestos para revisarlo.
      const pres = cerrado?.presupuesto_creado;
      if (pres) {
        toast.success(
          `Episodio cerrado. Presupuesto ${pres.number} preparado (${pres.total} €) — revísalo en Presupuestos`,
          { duration: 8000 },
        );
      } else {
        toast.success('Episodio cerrado');
      }
      setConfirmOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cerrar el episodio');
    } finally {
      setClosing(false);
    }
  };

  return (
    <Card className="cp-card p-5 border-2 border-[#2E6E5E] bg-[rgba(46,110,94,0.04)]" data-testid="episodio-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="font-display text-xl font-semibold text-[#13231E]">{formatDate(episodio.date)}</div>
          <div className="text-sm text-muted-foreground">Dr. {episodio.doctor_name || '—'}</div>
        </div>
        <div className="flex items-center gap-2">
          {episodio.cita_id && (
            <Badge variant="secondary" className="bg-[#2E6E5E]/15 text-[#13231E] border-[#2E6E5E]/30">
              <CalendarDays className="w-3 h-3 mr-1" /> Cita
            </Badge>
          )}
          <Badge className="bg-[#2E6E5E] text-white hover:bg-[#2E6E5E]">ABIERTO</Badge>
        </div>
      </div>

      {/* Corregir la historia de otro NO la convierte en tuya (2026-09-17):
          el PUT del episodio solo manda el texto, así que el autor, su nombre
          y su firma se quedan como estaban. Se dice aquí, encima del recuadro,
          para que quien escribe sepa en la nota de quién está escribiendo. */}
      {ajeno && (
        <div className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
          data-testid="episodio-editor-ajeno">
          Historia de {episodio.doctor_name || 'otro doctor'}: puedes corregirla, pero sigue siendo suya —
          la firma y el nombre del médico no cambian.
        </div>
      )}

      {/* Aviso de que lo escrito NO está guardado. Va DENTRO de la tarjeta y
          NO en un diálogo a propósito: el médico está en consulta con el
          paciente delante y un modal le robaría el foco y el teclado. Lo suyo
          se queda intacto en el recuadro de abajo; aquí solo se le enseña lo
          que hay guardado y se le dan las salidas. */}
      {aviso && (
        <div
          className="mb-3 rounded-md border-2 border-amber-500 bg-amber-50 px-3 py-2.5 text-amber-950"
          role="status"
          aria-live="polite"
          data-testid="episodio-aviso-sin-guardar"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm">
                {aviso.tipo === 'fallo' ? 'No se ha podido guardar' : 'Tu texto NO se ha guardado'}
              </div>
              <p className="text-xs mt-0.5">{aviso.mensaje}</p>

              {aviso.tipo === 'conflicto' && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider font-medium text-amber-800">
                    Lo que hay guardado ahora — {aviso.quien}
                  </div>
                  <div
                    className="mt-1 text-xs whitespace-pre-wrap bg-white border border-amber-300 rounded-md p-2 max-h-40 overflow-y-auto"
                    data-testid="episodio-conflicto-servidor"
                  >
                    {aviso.textoServidor || '(sin texto)'}
                  </div>
                  <p className="text-xs mt-2">
                    Lo tuyo sigue aquí abajo, sin tocar. Copia de arriba lo que te falte y guarda
                    encima, o quédate con lo de {aviso.quien} — y entonces lo tuyo se pierde.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-2">
                {aviso.tipo === 'conflicto' && (
                  <>
                    <Button
                      size="sm"
                      className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                      onClick={guardarLoMioEncima}
                      data-testid="episodio-conflicto-guardar-lo-mio"
                    >
                      Guardar lo mío encima
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={quedarmeConLoGuardado}
                      data-testid="episodio-conflicto-quedarme-lo-guardado"
                    >
                      Quedarme con lo de {aviso.quien}
                    </Button>
                  </>
                )}
                {aviso.tipo === 'fallo' && (
                  <Button
                    size="sm"
                    className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                    onClick={reintentarGuardado}
                    data-testid="episodio-aviso-reintentar"
                  >
                    Volver a intentarlo
                  </Button>
                )}
                {aviso.tipo === 'bloqueado' && (
                  <Button size="sm" variant="outline" onClick={copiarLoMio} data-testid="episodio-aviso-copiar">
                    Copiar mi texto
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={cerrarAviso} data-testid="episodio-aviso-cerrar">
                  Quitar el aviso
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lo que no llegó a guardarse la última vez (se quedó en este
          navegador). No se mete solo en el recuadro: eso sería pisar otra vez. */}
      {rescate && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50/70 px-3 py-2 text-amber-950"
          data-testid="episodio-rescate"
        >
          <div className="font-semibold text-sm">Esto se quedó sin guardar la última vez</div>
          <div
            className="mt-1 text-xs whitespace-pre-wrap bg-white border border-amber-200 rounded-md p-2 max-h-32 overflow-y-auto"
            data-testid="episodio-rescate-texto"
          >
            {rescate.texto}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={usarElRescate} data-testid="episodio-rescate-usar">
              Usar este texto
            </Button>
            <Button size="sm" variant="ghost" onClick={descartarElRescate} data-testid="episodio-rescate-descartar">
              Descartar
            </Button>
          </div>
        </div>
      )}

      <Textarea
        ref={taRef}
        value={texto}
        onChange={onChangeText}
        onBlur={flush}
        placeholder="Evolución, exploración, plan…"
        className="resize-none overflow-hidden bg-white"
        data-testid="episodio-texto"
      />

      <div className="flex items-center justify-between gap-3 flex-wrap mt-3">
        <div className="text-xs text-muted-foreground min-h-[16px]" aria-live="polite">
          {aviso ? (
            <span className="inline-flex items-center gap-1 text-amber-800 font-medium" data-testid="episodio-sin-guardar">
              <AlertTriangle className="w-3 h-3" /> Sin guardar
            </span>
          ) : saving ? (
            <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>
          ) : saved ? (
            <span className="inline-flex items-center gap-1 text-green-700" data-testid="episodio-guardado"><Check className="w-3 h-3" /> Guardado ✓</span>
          ) : refrescado ? (
            <span className="inline-flex items-center gap-1" data-testid="episodio-refrescado">
              <RotateCcw className="w-3 h-3" /> Actualizado por {refrescado}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <MicButton
            onText={appendTexto}
            className="h-8 w-8 border border-input bg-white"
            title="Dictar el evolutivo"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={pedirLimpio}
            disabled={limpiando || !texto.trim()}
            title="La IA lo redacta como escribes tú en tus evolutivos"
            data-testid="episodio-pasar-a-limpio"
          >
            {limpiando
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Redactando…</>
              : <><Sparkles className="w-3.5 h-3.5 mr-1.5 text-[#2E6E5E]" /> Pasar a limpio</>}
          </Button>
          <Button size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" onClick={onManualSave} disabled={saving} data-testid="episodio-guardar">
            Guardar
          </Button>
          <Button
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={closing}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="episodio-cerrar"
          >
            <Lock className="w-3.5 h-3.5 mr-1.5" /> Cerrar episodio
          </Button>
        </div>
      </div>

      {/* Acto médico ligado al episodio (2026-08-25): el registro del médico */}
      <ActoMedicoEpisodio episodio={episodio} patient={patient} />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="episodio-cerrar-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar episodio?</AlertDialogTitle>
            <AlertDialogDescription>
              Al cerrar, el episodio quedará bloqueado con fecha {formatDate(episodio.date)} y no podrá editarse. ¿Cerrar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onConfirmClose(); }}
              disabled={closing}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="episodio-cerrar-confirmar"
            >
              {closing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cerrar episodio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revisión antes de sustituir: la nota clínica no se toca sin que el
          doctor haya visto lo dictado y lo redactado uno al lado del otro. */}
      <AlertDialog open={propuesta != null} onOpenChange={(v) => { if (!v) setPropuesta(null); }}>
        <AlertDialogContent className="max-w-4xl" data-testid="episodio-limpio-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Evolutivo pasado a limpio</AlertDialogTitle>
            <AlertDialogDescription>
              {ejemplos > 0
                ? `Redactado imitando cómo escribes tú, mirando tus ${ejemplos} evolutivos anteriores. `
                : 'Todavía no tienes evolutivos cerrados de los que aprender tu estilo, así que de momento solo se ha puntuado y ordenado. '}
              Repásalo: la IA no añade nada que no hayas dictado, pero la nota es tuya.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                Lo que dictaste
              </div>
              <div className="text-xs whitespace-pre-wrap border border-border rounded-md p-3 bg-muted/30 max-h-72 overflow-y-auto">
                {texto}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                Redactado
              </div>
              <div className="text-xs whitespace-pre-wrap border-2 border-[#2E6E5E] rounded-md p-3 bg-[rgba(46,110,94,0.04)] max-h-72 overflow-y-auto"
                data-testid="episodio-limpio-propuesta">
                {propuesta}
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="episodio-limpio-descartar">Dejarlo como está</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); aceptarLimpio(); }}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="episodio-limpio-aceptar"
            >
              Usar el redactado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* La grabación de la consulta de este episodio (26/9/2026). */}
      {pie}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// c) Timeline de episodios
// ---------------------------------------------------------------------------

function TextoClamp({ text }) {
  const [expanded, setExpanded] = useState(false);
  const t = text || '';
  if (!t.trim()) return <p className="text-sm text-muted-foreground italic">Sin texto.</p>;
  const isLong = t.length > 280 || t.split('\n').length > 4;
  return (
    <div>
      <p
        className="text-sm whitespace-pre-wrap"
        style={!expanded && isLong
          ? { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
          : undefined}
      >
        {t}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 inline-flex items-center gap-1 text-xs text-[#2E6E5E] hover:text-[#13231E] transition-colors"
        >
          {expanded ? <><ChevronUp className="w-3 h-3" /> Ver menos</> : <><ChevronDown className="w-3 h-3" /> Ver más</>}
        </button>
      )}
    </div>
  );
}

function EpisodioTimelineCard({
  episodio, user, puedeEditar, onChanged, grabaciones = [], onGrabacionesCambian, paciente = null,
}) {
  const [reopening, setReopening] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const abierto = episodio.status === 'abierto';
  // Lo mismo que mira el servidor: ser administrador (D4, 22/9/2026:
  // los episodios solo los borran administradores, igual que gestionar
  // usuarios). La marca vieja `puede_borrar_episodios` ya no cuenta: a
  // dirección o a una doctora con la marca puesta se les pintaría el botón
  // rojo para recibir un «Permisos insuficientes» al pulsarlo.
  const puedeBorrar = isAdmin(user);

  const onReopen = async () => {
    setReopening(true);
    try {
      await reopenEpisodio(episodio.id);
      toast.success('Episodio reabierto');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reabrir el episodio');
    } finally {
      setReopening(false);
    }
  };

  const onBorrar = async () => {
    const dia = episodio.date || 'sin fecha';
    const motivo = window.prompt(
      `¿Borrar el episodio del ${dia} de la historia de este paciente?\n\n`
      + 'Dejará de salir en su ficha. Queda guardado quién lo borró, cuándo y '
      + 'por qué: es historia clínica.\n\nMotivo (por ejemplo: «lo apunté en la '
      + 'paciente equivocada»):', '');
    if (motivo === null) return;          // canceló
    setBorrando(true);
    try {
      await borrarEpisodio(episodio.id, { motivo });
      toast.success('Episodio borrado de la historia');
      onChanged?.();
    } catch (e) {
      // Si arrastra un presupuesto o actos de un colaborador, el servidor no
      // borra a la primera: dice qué cuelga y se vuelve a preguntar.
      const arrastra = e?.response?.data?.arrastra;
      if (e?.response?.status === 409 && arrastra) {
        const cuantos = (arrastra.presupuestos || []).length;
        const actos = (arrastra.actos_de_colaborador || []).length;
        // Las grabaciones de consulta (26/9/2026) SÍ se van con el episodio.
        const grabadas = (arrastra.grabaciones || []).length;
        const partes = [
          cuantos ? `${cuantos} presupuesto${cuantos === 1 ? '' : 's'}` : '',
          actos ? `${actos} acto${actos === 1 ? '' : 's'} de colaborador` : '',
        ].filter(Boolean).join(' y ');
        const lineas = [];
        if (partes) {
          lineas.push(`De este episodio cuelga ${partes}. Eso NO se borra: se queda sin episodio al que apuntar.`);
        }
        if (grabadas) {
          lineas.push(`Tiene ${grabadas} grabación${grabadas === 1 ? '' : 'es'} de la consulta: `
            + `se ${grabadas === 1 ? 'borra' : 'borran'} con el episodio, audio incluido, y no se `
            + 'pueden recuperar.');
        }
        if (window.confirm(`${lineas.join('\n\n')}\n\n¿Borrar el episodio de todas formas?`)) {
          try {
            await borrarEpisodio(episodio.id, { aun_asi: true, motivo });
            toast.success('Episodio borrado de la historia');
            onChanged?.();
            if (grabadas) onGrabacionesCambian?.();
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || 'No se pudo borrar');
          }
        }
      } else {
        toast.error(e?.response?.data?.detail || 'No se pudo borrar el episodio');
      }
    } finally {
      setBorrando(false);
    }
  };

  return (
    <Card className="cp-card p-4" data-testid="episodio-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <span className="font-semibold text-[#13231E]">{formatDate(episodio.date)}</span>
          <span className="text-sm text-muted-foreground ml-2">Dr. {episodio.doctor_name || '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          {episodio.cita_id && (
            <Badge variant="secondary" className="bg-[#2E6E5E]/15 text-[#13231E] border-[#2E6E5E]/30">
              <CalendarDays className="w-3 h-3 mr-1" /> Cita
            </Badge>
          )}
          {abierto ? (
            <Badge className="bg-[#2E6E5E] text-white hover:bg-[#2E6E5E]">ABIERTO</Badge>
          ) : (
            <Badge variant="secondary" className="bg-muted text-muted-foreground border border-border">
              <Lock className="w-3 h-3 mr-1" /> CERRADO
            </Badge>
          )}
        </div>
      </div>

      <TextoClamp text={episodio.texto} />

      {/* El cartel depende de si ESTE usuario puede editarlo, no de si el
          episodio es suyo (2026-09-17): antes al admin se le decía «solo
          lectura» en la historia abierta de un médico aunque el servidor sí le
          dejaba corregirla. Quien puede editar no ve esta tarjeta: su episodio
          sale arriba, ya abierto en el editor. */}
      {abierto && !puedeEditar && (
        <div className="mt-2 text-xs text-muted-foreground italic">
          Episodio abierto por {episodio.doctor_name || 'otro doctor'} — solo lectura.
        </div>
      )}

      {(!abierto && isAdmin(user)) || puedeBorrar ? (
        <div className="mt-2 flex items-center gap-2">
          {!abierto && isAdmin(user) && (
            <Button size="sm" variant="outline" onClick={onReopen} disabled={reopening} data-testid="episodio-reabrir">
              {reopening ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
              Reabrir
            </Button>
          )}
          {puedeBorrar && (
            <Button size="sm" variant="ghost" onClick={onBorrar} disabled={borrando}
              className="text-red-700 hover:text-red-800" data-testid="episodio-borrar">
              {borrando ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
              Borrar
            </Button>
          )}
        </div>
      ) : null}

      {/* La grabación de la consulta de este episodio (26/9/2026): también en
          el episodio de otro (enfermería graba la consulta de un médico). */}
      <GrabacionConsulta episodio={episodio} paciente={paciente} user={user} grabaciones={grabaciones}
        onCambio={onGrabacionesCambian} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// d) Citas del paciente
// ---------------------------------------------------------------------------

function CitaRow({ cita, agendaName, agendaVisible, onAbrir, onPdf, onDeshacerNoAcude }) {
  const cancelada = cita.status === 'cancelada';
  const noAcude = cita.status === 'no_acude';
  // Entrar en la cita desde la ficha (2026-09-09): la fila es un
  // botón de verdad — cursor de mano, foco de teclado y Enter/espacio del
  // navegador — y lleva a la agenda de ese día con el resumen de la cita ya
  // abierto, que es como se ve el detalle de una cita en toda la aplicación.
  // Dos citas no llevan a ninguna parte, y entonces la fila NO es un botón (no
  // promete con el cursor algo que no va a pasar) y dice por qué al posarse:
  //   - anulada: ya no está en ninguna agenda, y lo que hay que saber de ella
  //     (quién la anuló, cuándo y por qué) está aquí mismo, en su etiqueta;
  //   - agenda que este usuario no ve: en la ficha salen TODAS las citas del
  //     paciente (un doctor ve también las de sus compañeros), pero esa agenda
  //     no puede abrirla.
  const puedeAbrir = !!onAbrir && !cancelada && agendaVisible;
  const info = (
    <>
      <div className={`text-sm font-medium text-[#13231E] truncate ${puedeAbrir ? 'transition-colors group-hover:text-[#2E6E5E]' : ''}`}>
        {cita.concept_name || cita.concept_code || 'Cita'}
      </div>
      <div className="text-xs text-muted-foreground">
        {formatDate(cita.date)} · {cita.start}{agendaName ? ` · ${agendaName}` : ''}
      </div>
    </>
  );
  return (
    <div className={`py-2 flex items-center justify-between gap-3 ${cancelada ? 'opacity-60' : ''}`} data-testid="paciente-cita-row">
      {puedeAbrir ? (
        <button
          type="button"
          onClick={() => onAbrir(cita)}
          title="Abrir la cita en la agenda de ese día"
          data-testid={`paciente-cita-abrir-${cita.id}`}
          className="group min-w-0 flex-1 text-left cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {info}
        </button>
      ) : (
        <div
          className={`min-w-0 flex-1 ${cancelada ? 'line-through' : ''}`}
          title={cancelada ? 'Cita anulada: ya no está en la agenda' : 'No tienes acceso a la agenda de esta cita'}
        >
          {info}
        </div>
      )}
      {noAcude ? (
        <span className="inline-flex items-center gap-1.5 shrink-0">
        <Badge
          variant="secondary"
          className="bg-amber-100 text-amber-900 border border-amber-300"
          title={[
            cita.no_acude_by_name ? `Marcado por ${cita.no_acude_by_name}` : 'No acudió',
            cita.no_acude_at ? `el ${formatDate(cita.no_acude_at.slice(0, 10))}` : '',
          ].filter(Boolean).join(' ')}
        >
          No acude
        </Badge>
        {onDeshacerNoAcude && (
          <button
            type="button"
            onClick={() => onDeshacerNoAcude(cita)}
            className="text-[11px] text-[#2E6E5E] underline underline-offset-2"
            title="Marcado por error: la cita vuelve a estar activa"
            data-testid={`cita-deshacer-no-acude-${cita.id}`}
          >
            Deshacer
          </button>
        )}
        </span>
      ) : cancelada ? (
        <Badge
          variant="secondary"
          className="bg-muted text-muted-foreground border border-border no-underline"
          title={[
            cita.cancelled_by_name ? `Anulada por ${cita.cancelled_by_name}` : 'Anulada',
            cita.cancelled_at ? `el ${formatDate(cita.cancelled_at.slice(0, 10))}` : '',
            cita.cancel_reason ? `— ${cita.cancel_reason}` : '',
          ].filter(Boolean).join(' ')}
        >
          Anulada{cita.cancelled_at ? ` · ${formatDate(cita.cancelled_at.slice(0, 10))}` : ''}
        </Badge>
      ) : (
        <div className="flex items-center gap-1 shrink-0">
          {/* Los WhatsApp al paciente salen SOLO desde la ficha de la cita y
              desde Recordatorios (2026-08-25: una tercera puerta
              aquí confundía y era redundante). */}
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
            title="PDF de la cita (tarjeta / justificante)"
            onClick={() => onPdf(cita)}
            data-testid={`cita-pdf-${cita.id}`}
          >
            <Printer className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function CitasCard({ patientId, refreshKey }) {
  const navigate = useNavigate();
  const [citas, setCitas] = useState([]);
  const [agendaNames, setAgendaNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // {url, filename, title} | null
  const [tick, setTick] = useState(0); // recarga tras deshacer un «no acude»
  const deshacerNoAcude = async (cita) => {
    if (!window.confirm('¿Deshacer el «No acude»? La cita volverá a estar activa.')) return;
    try {
      await api.post(`/api/citas/${cita.id}/no-acude/deshacer`);
      toast.success('Cita reactivada');
      setTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo deshacer');
    }
  };

  // Abrir la cita: a la agenda de su día, con su agenda seleccionada y el
  // resumen desplegado (2026-09-09). No se inventa otra pantalla:
  // /citas sabe leer estos tres datos y hace justo eso. La fecha y la agenda
  // van en la dirección para no tener que buscar la cita por todo el calendario.
  const abrirEnAgenda = (cita) => {
    const q = new URLSearchParams({ cita: cita.id, fecha: cita.date, agenda: cita.agenda_id || '' });
    navigate(`/citas?${q.toString()}`);
  };

  // PDF de la cita (tarjeta/justificante) en el visor de siempre.
  const abrirPdf = (cita) => {
    setPdfPreview({
      url: `${BACKEND_URL}/api/citas/${cita.id}/pdf`,
      filename: `cita_${cita.date}_${(cita.start || '').replace(':', '')}.pdf`,
      title: `Cita · ${formatDate(cita.date)} ${cita.start}`,
    });
  };

  useEffect(() => {
    if (!patientId) return;
    let alive = true;
    setLoading(true);
    Promise.all([
      // Con las anuladas incluidas: el histórico del paciente debe contarlo
      // todo, también qué citas se cancelaron (salen tachadas).
      listCitas({ patientId, includeCancelled: true }),
      listAgendas().catch(() => []),
    ])
      .then(([cs, ags]) => {
        if (!alive) return;
        setCitas(Array.isArray(cs) ? cs : []);
        const map = {};
        (Array.isArray(ags) ? ags : []).forEach((a) => { map[a.id] = a.name; });
        setAgendaNames(map);
      })
      .catch(() => { if (alive) setCitas([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [patientId, refreshKey, tick]);

  const hoy = todayStr();
  const proximas = citas
    .filter((c) => c.date >= hoy)
    .sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start));
  const pasadas = citas
    .filter((c) => c.date < hoy)
    .sort((a, b) => (b.date + b.start).localeCompare(a.date + a.start));

  return (
    <SectionCard title="Citas" className="mt-6">
      {loading ? (
        <div className="py-4 text-center text-sm text-muted-foreground">Cargando citas…</div>
      ) : citas.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin citas.</p>
      ) : (
        <div data-testid="paciente-citas">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">Próximas</div>
          {proximas.length === 0 ? (
            <p className="text-sm text-muted-foreground py-1">Sin citas próximas.</p>
          ) : (
            <div className="divide-y divide-border">
              {proximas.map((c) => <CitaRow key={c.id} cita={c} agendaName={agendaNames[c.agenda_id]} agendaVisible={!!agendaNames[c.agenda_id]} onAbrir={abrirEnAgenda} onPdf={abrirPdf} onDeshacerNoAcude={deshacerNoAcude} />)}
            </div>
          )}

          {pasadas.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowPast((v) => !v)}
                className="inline-flex items-center gap-1 text-sm text-[#2E6E5E] hover:text-[#13231E] transition-colors"
                data-testid="paciente-citas-ver-anteriores"
              >
                {showPast ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {showPast ? 'Ocultar anteriores' : `Ver anteriores (${pasadas.length})`}
              </button>
              {showPast && (
                <div className="divide-y divide-border mt-1">
                  {pasadas.map((c) => <CitaRow key={c.id} cita={c} agendaName={agendaNames[c.agenda_id]} agendaVisible={!!agendaNames[c.agenda_id]} onAbrir={abrirEnAgenda} onPdf={abrirPdf} onDeshacerNoAcude={deshacerNoAcude} />)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <PdfPreviewDialog
        open={!!pdfPreview}
        onOpenChange={(o) => !o && setPdfPreview(null)}
        url={pdfPreview?.url}
        filename={pdfPreview?.filename}
        title={pdfPreview?.title}
      />
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function HistoriaClinicaTab({ patient, onPatientUpdated, refreshKey, notasSlot }) {
  const { user } = useAuth();
  const [episodios, setEpisodios] = useState([]);
  const [loadingEp, setLoadingEp] = useState(true);
  const [creating, setCreating] = useState(false);

  const role = normalizeRole(user?.role);
  const canCreateEpisodio = role === 'admin' || role === 'direccion' || role === 'doctor' || role === 'enfermeria';
  const patientId = patient?.id;

  const reloadEpisodios = useCallback(async () => {
    if (!patientId) return;
    try {
      const eps = await listEpisodios(patientId);
      setEpisodios(Array.isArray(eps) ? eps : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los episodios');
    } finally {
      setLoadingEp(false);
    }
  }, [patientId]);

  useEffect(() => {
    setLoadingEp(true);
    reloadEpisodios();
  }, [reloadEpisodios]);

  // Las grabaciones de consulta de este paciente QUE ESTA PERSONA PUEDE OÍR
  // (las suyas; un admin, todas): una sola petición para todos los episodios.
  // A quien no graba no se le pide nada: el servidor no le daría ninguna.
  const puedeGrabar = puedeGrabarConsulta(user);
  const [grabaciones, setGrabaciones] = useState([]);
  const recargarGrabaciones = useCallback(async () => {
    if (!patientId || !puedeGrabar) {
      setGrabaciones([]);
      return;
    }
    try {
      setGrabaciones(await listarGrabacionesDelPaciente(patientId));
    } catch (e) {
      setGrabaciones([]);
    }
  }, [patientId, puedeGrabar]);
  // Al terminar una grabación (aunque se empezara con esta pantalla montada
  // otra vez, o desde otra), la lista se recarga sola: sale sin recargar.
  const grabacionesTerminadas = useGrabacionesTerminadas();
  useEffect(() => { recargarGrabaciones(); }, [recargarGrabaciones, grabacionesTerminadas]);
  const grabacionesDe = (episodioId) => grabaciones.filter((g) => g.episodio_id === episodioId);
  // Para la barra flotante de la grabación: de quién es la consulta.
  const pacienteDeLaGrabacion = {
    id: patientId,
    nombre: [patient?.name, patient?.surname1, patient?.surname2].filter(Boolean).join(' '),
  };

  // Los 3 datos clínicos obligatorios deben estar registrados para poder
  // abrir un evolutivo (el backend también lo valida).
  const datosObligatoriosOk =
    patient?.alergias !== null && patient?.alergias !== undefined
    && !!(patient?.medicacion_habitual || '').trim()
    && patient?.ah_previo !== null && patient?.ah_previo !== undefined;

  const onNuevoEpisodio = async () => {
    setCreating(true);
    try {
      await createEpisodio({ patient_id: patient.id });
      await reloadEpisodios();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear el episodio');
    } finally {
      setCreating(false);
    }
  };

  // Historia completa: datos clínicos + todos los evolutivos por fecha,
  // como informe editable e imprimible (módulo de Informes).
  const [generandoHistoria, setGenerandoHistoria] = useState(false);
  const navigate = useNavigate();

  // Alergias y ácido hialurónico previo, tal y como se van a IMPRIMIR en un
  // informe que sale de la clínica. La casilla no manda a solas
  // (2026-09-21): con {alergias: false, alergias_text: 'LATEX (nota quirófano
  // 2026-08-20)'} esto escribía «Alergias: No» en un papel firmado por el
  // doctor, con el látex en la ficha de la que se compone ese papel. Se dicen
  // LAS DOS COSAS, con las mismas palabras que el informe que arma el servidor
  // (`backend/routers/informes_router._si_no_texto`), y quién decide si lo
  // escrito apunta un riesgo es la regla de siempre (`lib/riesgoClinico`).
  const triTexto = (v, detalle) => {
    const contradice = textoQueContradice(v, detalle);
    if (contradice) return `No — y aun así consta: ${contradice}`;
    const escrito = (detalle || '').trim();
    if (v === true) return `SÍ${escrito ? ` — ${escrito}` : ''}`;
    if (v === false) return 'No';
    // Sin contestar pero con algo escrito: lo escrito es lo único que hay.
    return escrito && hayRiesgoEscrito(escrito)
      ? `No registrado, y consta: ${escrito}` : 'No registrado';
  };

  const onHistoriaCompleta = async () => {
    setGenerandoHistoria(true);
    try {
      // Cada dato en su propio párrafo (línea en blanco entre medias): el
      // markdown ligero del PDF une los saltos de línea simples.
      const L = [];
      L.push('**DATOS CLÍNICOS**');
      L.push('');
      L.push(`Alergias: ${triTexto(patient.alergias, patient.alergias_text)}`);
      L.push('');
      L.push(`Medicación habitual: ${(patient.medicacion_habitual || '').trim() || 'No registrada'}`);
      if ((patient.antecedentes_medicos || '').trim()) {
        L.push('');
        L.push(`Antecedentes médicos: ${patient.antecedentes_medicos.trim()}`);
      }
      L.push('');
      L.push(`Ácido hialurónico previo: ${triTexto(patient.ah_previo, patient.ah_previo_text)}`);
      if ((patient.historia_general || '').trim()) {
        L.push('');
        L.push(`Historia general: ${patient.historia_general.trim()}`);
      }
      L.push('');
      L.push('**EVOLUTIVOS**');
      // Releer del servidor: el editor abierto autoguarda y el estado local
      // puede no tener el último texto.
      const frescos = await listEpisodios(patient.id);
      const orden = [...(Array.isArray(frescos) ? frescos : [])].sort(
        (a, b) => `${a.date}${a.created_at}`.localeCompare(`${b.date}${b.created_at}`),
      );
      if (orden.length === 0) {
        L.push('Sin evolutivos registrados.');
      }
      orden.forEach((e) => {
        L.push('');
        L.push(`**${formatDate(e.date)} — ${e.doctor_name || '—'}**`);
        L.push((e.texto || '').trim() || '(sin texto)');
      });

      const res = await api.post('/api/informes', {
        patient_id: patient.id,
        title: `Historia clínica — ${[patient.name, patient.surname1, patient.surname2].filter(Boolean).join(' ')}`,
        body: L.join('\n'),
        doctor_name: user?.name || '',
      });
      toast.success('Historia completa generada — edítala y imprímela desde aquí');
      navigate(`/informes?edit=${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo generar la historia completa');
    } finally {
      setGenerandoHistoria(false);
    }
  };

  // Visita de hoy → informe imprimible con los datos clínicos y el/los
  // evolutivos de la fecha de hoy (mismo mecanismo que la historia completa).
  const [generandoVisita, setGenerandoVisita] = useState(false);

  const onVisitaHoy = async () => {
    setGenerandoVisita(true);
    try {
      const frescos = await listEpisodios(patient.id);
      const hoy = todayStr();
      const deHoy = (Array.isArray(frescos) ? frescos : [])
        .filter((e) => e.date === hoy)
        .sort((a, b) => `${a.created_at}`.localeCompare(`${b.created_at}`));
      if (deHoy.length === 0) {
        toast.error('Este paciente no tiene episodio de hoy — crea el evolutivo primero');
        return;
      }
      const L = [];
      L.push(`**VISITA DEL ${formatDate(hoy)}**`);
      L.push('');
      L.push(`Alergias: ${triTexto(patient.alergias, patient.alergias_text)}`);
      L.push('');
      L.push(`Medicación habitual: ${(patient.medicacion_habitual || '').trim() || 'No registrada'}`);
      if ((patient.antecedentes_medicos || '').trim()) {
        L.push('');
        L.push(`Antecedentes médicos: ${patient.antecedentes_medicos.trim()}`);
      }
      deHoy.forEach((e) => {
        L.push('');
        L.push(`**${e.doctor_name || '—'}**`);
        L.push((e.texto || '').trim() || '(sin texto)');
      });
      const res = await api.post('/api/informes', {
        patient_id: patient.id,
        title: `Visita ${formatDate(hoy)} — ${[patient.name, patient.surname1, patient.surname2].filter(Boolean).join(' ')}`,
        body: L.join('\n'),
        doctor_name: user?.name || '',
      });
      toast.success('Visita de hoy lista — revísala e imprímela');
      navigate(`/informes?edit=${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo generar la visita de hoy');
    } finally {
      setGenerandoVisita(false);
    }
  };

  // Episodios abiertos que ESTE usuario puede editar (normalmente 0 o 1):
  // arriba, en modo edición. Son el suyo y —si es admin— el de cualquiera.
  //
  // Lo segundo es lo nuevo (2026-09-17: «necesitamos poder abrir y
  // modificar los episodios que escriben los médicos desde la ficha»). No es
  // un permiso nuevo: el servidor ya lo daba (`_is_author_or_admin` en
  // /api/episodios deja editar un episodio abierto al autor O a cualquier
  // admin) y era esta pantalla, más estricta que él, la que le enseñaba el
  // cartel de «solo lectura». Corregirlo NO se lo apropia: el PUT solo manda
  // `texto`, así que el autor (`doctor_user_id`), su nombre y su firma siguen
  // siendo los del médico que lo escribió; en el editor se dice con todas las
  // letras (`ajeno`). Un administrativo que no sea admin sigue sin poder.
  //
  // Esta regla ya incluye el episodio automático de cirugía (origen
  // `auto_cirugia`, 2026-09-08: «déjalo abierto»), que nace ABIERTO a
  // nombre del cirujano aunque lo marque enfermería: si el cirujano es usuario
  // de la app lo completa y cierra él, y si no lo es (quirófano de externos,
  // `doctor_user_id` vacío) lo hace un admin, que antes entraba por una
  // excepción y ahora entra por la regla general.
  //
  // Lo CERRADO no cambia: para tocarlo sigue habiendo que pulsar «Reabrir»,
  // que solo ve el admin.
  const puedeEditarAbierto = (e) => e.status === 'abierto'
    && (e.doctor_user_id === user?.id || isAdmin(user));
  const misAbiertos = episodios.filter(puedeEditarAbierto);
  const resto = episodios.filter((e) => !misAbiertos.some((m) => m.id === e.id));
  // Con un evolutivo abierto, la cara se pinta a su derecha y NO abajo.
  const hayEvolutivoAbierto = misAbiertos.length > 0;

  // Primera consulta = el paciente aún no tiene ninguna visita cerrada. El IVA
  // del tratamiento solo se marca aquí; en revisiones posteriores se oculta.
  const esPrimeraConsulta = !episodios.some((e) => e.status === 'cerrado');

  return (
    <div className="space-y-4" data-testid="historia-clinica-tab">
      {/* Franja superior compacta: notas (si las hay) + datos clínicos, para
          que la historia (episodios) quede visible sin hacer scroll. */}
      {notasSlot ? (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
          <div className="xl:col-span-2"><DatosClinicosCard patient={patient} onPatientUpdated={onPatientUpdated} esPrimeraConsulta={esPrimeraConsulta} /></div>
          <div>{notasSlot}</div>
        </div>
      ) : (
        <DatosClinicosCard patient={patient} onPatientUpdated={onPatientUpdated} esPrimeraConsulta={esPrimeraConsulta} />
      )}

      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-display font-semibold text-[#13231E] text-base">Episodios</h3>
          <div className="flex items-center gap-2">
            <Button
              onClick={onVisitaHoy}
              disabled={generandoVisita || loadingEp}
              variant="outline"
              data-testid="episodio-imprimir-visita"
            >
              {generandoVisita ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
              Imprimir visita de hoy
            </Button>
            {canCreateEpisodio && (
              <Button
                onClick={onNuevoEpisodio}
                disabled={creating}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="episodio-nuevo"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Nuevo episodio (hoy)
              </Button>
            )}
          </div>
        </div>

        {loadingEp ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Cargando episodios…</div>
        ) : (
          <div className="space-y-3">
            {/* Con un evolutivo abierto: el texto a la IZQUIERDA y la cara a la
                DERECHA, a la vez y sin bajar la pantalla (2026-08-19).
                La cara se queda pegada al hacer scroll del evolutivo largo. En
                móvil vuelve a ser una debajo de otra, que si no no cabe. */}
            {misAbiertos.length > 0 ? (
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,30rem)_minmax(0,1fr)] gap-4 items-start"
                data-testid="evolutivo-con-mapa">
                {/* El evolutivo, estrecho (tope 30rem); la CARA se lleva el
                    resto para marcar los puntos con precisión (2026-08-19). */}
                <div className="space-y-3 min-w-0">
                  {misAbiertos.map((e, index) => (
                    <React.Fragment key={e.id}>
                      <EpisodioEditor
                        episodio={e}
                        patient={patient}
                        ajeno={e.doctor_user_id !== user?.id}
                        onChanged={reloadEpisodios}
                        pie={(
                          <GrabacionConsulta episodio={e} paciente={pacienteDeLaGrabacion} user={user}
                            grabaciones={grabacionesDe(e.id)} onCambio={recargarGrabaciones} />
                        )}
                      />
                      {index === 0 && esPrimeraConsulta && (
                        <MarcaIvaPrimeraValoracion patient={patient} episodio={e} onPatientUpdated={onPatientUpdated} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="min-w-0 xl:sticky xl:top-4" data-columna-mapa>
                  <SpecialtyMap patient={patient} onPatientUpdated={onPatientUpdated} apilado />
                </div>
              </div>
            ) : null}

            {episodios.length === 0 && (
              <Card className="cp-card p-6 text-center">
                <History className="w-8 h-8 text-[#2E6E5E] mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Sin episodios todavía.{canCreateEpisodio ? ' Crea el primero con "Nuevo episodio (hoy)".' : ''}
                </p>
              </Card>
            )}

            {resto.map((e) => (
              <EpisodioTimelineCard
                key={e.id}
                episodio={e}
                user={user}
                puedeEditar={puedeEditarAbierto(e)}
                onChanged={reloadEpisodios}
                grabaciones={grabacionesDe(e.id)}
                onGrabacionesCambian={recargarGrabaciones}
                paciente={pacienteDeLaGrabacion}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fotos del paciente (subir/ver antes-después) accesibles desde la propia
          historia, con botón directo — sin ir a otra pestaña. */}
      <PatientPhotosTab patient={patient} />

      {/* Mapa clínico persistente (no por episodio) según la especialidad:
          facial, odontograma o mapa corporal. Va aquí SOLO si no hay ningún
          evolutivo abierto: con uno abierto se pinta a su derecha. Dos copias
          a la vez tendrían cada una su estado y al guardar una pisaría a la
          otra (2026-08-19). */}
      {!hayEvolutivoAbierto && (
        <SpecialtyMap patient={patient} onPatientUpdated={onPatientUpdated} />
      )}

      <CitasCard patientId={patientId} refreshKey={refreshKey} />

      {/* Historia completa: datos + evolutivos → informe editable e imprimible */}
      <div className="flex justify-end">
        <Button
          onClick={onHistoriaCompleta}
          disabled={generandoHistoria || loadingEp}
          variant="outline"
          className="border-[#2E6E5E] text-[#13231E] hover:bg-[#2E6E5E] hover:text-white"
          data-testid="historia-completa-generar"
        >
          {generandoHistoria ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
          Historia completa (editar e imprimir)
        </Button>
      </div>
    </div>
  );
}
